import Decimal from "decimal.js";
import { z } from "zod/v4";

export const numericToLocaleString = z.number().transform((val, ctx) => {
  if (isNaN(val)) {
    ctx.addIssue({
      code: "custom",
      message: "Not a Number",
    });
    // This is a special symbol you can use to
    // return early from the transform function.
    // It has type `never` so it does not affect the
    // inferred return type.
    return z.NEVER;
  }

  return val.toLocaleString("en-us", {
    minimumFractionDigits: 2,
  });
});
export const numericObjectToLocaleString = z
  .object({
    numDigits: z.number().optional(),
    maxDigits: z.number().optional().default(6),
    minNumberOfFractionZeros: z.number().optional().default(3),
    value: z.number().nullable(),
  })
  .transform((val) => {
    if (val.value === null || isNaN(val.value)) return "-";

    // Helper function to check if a number is very close to its rounded value
    // const isVeryClose = (num: number, precision: number) => {
    //   const rounded =
    //     Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision);
    //   return Math.abs(num - rounded) < Math.pow(10, -(precision + 1));
    // };

    // For zero, return simple formatted zero
    if (val.value === 0) {
      return "0.00";
    }

    // Handle very large numbers
    const veryLargeThreshold = 1e8; // You can adjust this threshold if needed
    if (Math.abs(val.value) >= veryLargeThreshold) {
      const scientificStr = val.value.toExponential(3); // limit significant figures
      return scientificStr.replace("e+", " × 10^").replace("e-", " × 10^-");
    }

    // Handle very small numbers first
    if (Math.abs(val.value) < 0.001) {
      const scientificStr = val.value.toExponential();
      const [coefficient, exponent] = scientificStr.split("e");
      const absExponent = Math.abs(parseInt(exponent));
      const formattedCoef = Number(
        parseFloat(coefficient).toFixed(3),
      ).toLocaleString("en-us", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      });

      // Remove leading zero and decimal point from formatted coefficient
      const cleanCoef = formattedCoef.substring(2).replace(/[^\d]/, "");
      const cleanBeforeDecimal = formattedCoef
        .split(".")[0]
        .replace(/[^\d]/, "");
      if (absExponent > val.minNumberOfFractionZeros) {
        return `0.0<sub>${absExponent - 1}</sub>${cleanBeforeDecimal}${cleanCoef}`;
      }
    }

    // First check if the number is very close to a rounded value
    // for (let precision = 1; precision <= 3; precision++) {
    //   if (isVeryClose(val.value, precision)) {
    //     const rounded =
    //       Math.round(val.value * Math.pow(10, precision)) /
    //       Math.pow(10, precision);
    //     return rounded.toLocaleString("en-us", {
    //       minimumFractionDigits: Math.min(precision, 2),
    //       maximumFractionDigits: Math.min(precision, 2),
    //     });
    //   }
    // }

    const valueStrippedFromTrailingZeros = val.value
      .toString()
      .replace(/(\.[0-9]*[1-9])0+$/, "$1");

    // Check if there are many zeros after the decimal point
    const isLargeNumber = Math.abs(val.value) > 1;

    if (isLargeNumber) {
      return Number(valueStrippedFromTrailingZeros).toLocaleString("en-us", {
        minimumFractionDigits: 0,
        maximumFractionDigits: val.maxDigits === 0 ? 0 : 2,
      });
    }

    const numDigits = Math.min(
      val.numDigits ?? valueStrippedFromTrailingZeros.replace(".", "").length,
      val.maxDigits,
    );

    const formatted = Number(valueStrippedFromTrailingZeros).toLocaleString(
      "en-us",
      {
        minimumFractionDigits: numDigits,
        maximumFractionDigits: numDigits,
      },
    );

    // Handle numbers between 0 and 1
    if (val.value > 0 && val.value < 1) {
      let [, fractional] = formatted.split(".");

      // If the fractional part exists and is all 9s
      if (fractional && /^9+$/.test(fractional)) {
        const precision = Math.min(fractional.length, 6);
        const rounded =
          Math.round(val.value * Math.pow(10, precision)) /
          Math.pow(10, precision);
        return rounded.toLocaleString("en-us", {
          minimumFractionDigits: 3,
          maximumFractionDigits: 20,
        });
      }

      // Format small numbers with subscript notation
      if (fractional) {
        const leadingZeros = fractional.match(/^0+/);
        if (
          leadingZeros &&
          leadingZeros[0].length > val.minNumberOfFractionZeros
        ) {
          const significantPart = fractional.slice(leadingZeros[0].length);
          return `0.0<sub>${leadingZeros[0].length}</sub>${significantPart.slice(0, 4)}`;
        }
      }
    }

    return Number(val.value).toLocaleString("en-us", {
      minimumFractionDigits: Math.min(2, val.maxDigits),
      maximumFractionDigits: val.maxDigits,
    });
  });

export const numericToHumanString = z.number().transform((val, ctx) => {
  if (isNaN(val)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Not a Number",
    });
    // This is a special symbol you can use to
    // return early from the transform function.
    // It has type `never` so it does not affect the
    // inferred return type.
    return z.NEVER;
  }

  const SI_SYMBOL = ["", "K", "M", "B", "T", "P", "E"];
  const absVal = Math.abs(val);
  const tier = (Math.log10(absVal) / 3) | 0;
  if (tier === 0) {
    return val.toLocaleString();
  }
  const suffix = SI_SYMBOL[tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = val / scale;
  return `${scaled.toFixed(2)}${suffix ?? ""}`;
});

export function parseNumericObject(
  value: number | null,
  numDigits?: number,
  maxDigits = 6,
) {
  return numericObjectToLocaleString.parse({
    value,
    numDigits,
    maxDigits,
  });
}

export function getNumberOfFractionDigits(value: number) {
  const valueStr = value.toString();
  if (valueStr.includes("e")) {
    const [, fractional] = value.toExponential().split("e");
    return Math.abs(parseInt(fractional));
  }
  const [, fractional] = value.toString().split(".");

  return fractional?.length ?? 0;
}

export function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function coerceDecimal(value: number | string | Decimal) {
  if (value instanceof Decimal) {
    return value;
  }

  return new Decimal(value);
}
